import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AIChatbot.css';

const AIChatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [agentInfo, setAgentInfo] = useState(null);
  const [sampleLoans, setSampleLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingLoans, setIsLoadingLoans] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const API_URL = process.env.REACT_APP_CHATBOT_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Add welcome message
    setMessages([{
      id: '1',
      type: 'bot',
      content: "👋 Hello! I'm your AI Loan Assistant. I can help you query loan information, document statuses, and approval details. Try asking me about a specific loan!",
      timestamp: new Date()
    }]);

    // Fetch agent info
    fetchAgentInfo();
    
    // Fetch recent loans on initial load
    fetchRecentLoans();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Debounce search - wait 500ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLoans(searchTerm);
      }, 500);
    } else {
      // If search is cleared, load recent loans
      fetchRecentLoans();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    filterLoans();
  }, [statusFilter, docTypeFilter, companyFilter, sampleLoans]);

  const fetchAgentInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/info`);
      if (response.ok) {
        const data = await response.json();
        setAgentInfo(data);
      }
    } catch (error) {
      console.error('Error fetching agent info:', error);
    }
  };

  const fetchRecentLoans = async () => {
    try {
      setIsLoadingLoans(true);
      const response = await fetch(`${API_URL}/api/loans/recent?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setSampleLoans(data.loans || []);
        setFilteredLoans(data.loans || []);
      } else {
        console.error('Failed to fetch recent loans');
        setSampleLoans([]);
        setFilteredLoans([]);
      }
    } catch (error) {
      console.error('Error fetching recent loans:', error);
      setSampleLoans([]);
      setFilteredLoans([]);
    } finally {
      setIsLoadingLoans(false);
    }
  };

  const searchLoans = async (keyword) => {
    try {
      setIsLoadingLoans(true);
      const response = await fetch(`${API_URL}/api/loans/search?q=${encodeURIComponent(keyword)}&limit=100`);
      if (response.ok) {
        const data = await response.json();
        setSampleLoans(data.loans || []);
        setFilteredLoans(data.loans || []);
      } else {
        console.error('Failed to search loans');
        setSampleLoans([]);
        setFilteredLoans([]);
      }
    } catch (error) {
      console.error('Error searching loans:', error);
      setSampleLoans([]);
      setFilteredLoans([]);
    } finally {
      setIsLoadingLoans(false);
    }
  };

  const filterLoans = () => {
    let filtered = [...sampleLoans];
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(loan => loan.status.toLowerCase() === statusFilter);
    }
    
    // Filter by document type
    if (docTypeFilter !== 'all') {
      filtered = filtered.filter(loan => loan.type === docTypeFilter);
    }
    
    // Filter by company
    if (companyFilter !== 'all') {
      filtered = filtered.filter(loan => loan.company === companyFilter);
    }
    
    // Note: searchTerm is handled separately via API search
    
    setFilteredLoans(filtered);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const botMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: `❌ Sorry, I encountered an error: ${error.message}. Please make sure the chatbot API is running.`,
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = async () => {
    try {
      await fetch(`${API_URL}/api/chat/clear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });

      setMessages([{
        id: Date.now().toString(),
        type: 'bot',
        content: "✨ Chat cleared! How can I help you?",
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error clearing chat:', error);
      setMessages([{
        id: Date.now().toString(),
        type: 'bot',
        content: "✨ Chat cleared! How can I help you?",
        timestamp: new Date()
      }]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  const handleLoanClick = (loan) => {
    // Create a more natural query about the loan
    const query = `Tell me about loan ${loan.id}`;
    setInputMessage(query);
    inputRef.current?.focus();
  };

  const suggestions = agentInfo?.example_queries || [
    "List 5 recent documents",
    "What's the HOI deductible for loan L001?",
    "Search for documents from company 'gri'",
    "Show me approved documents"
  ];

  return (
    <div className="ai-chatbot-container">
      {/* Sample Loans Panel */}
      <div className="loans-panel">
        <div className="loans-panel-header">
          <h3>Sample Loans</h3>
          <span className="loans-count">{filteredLoans.length} loans</span>
        </div>
        
        <div className="loans-filters">
          <input
            type="text"
            placeholder="Search by loan #, GUID, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="loan-search"
          />
          
          <div className="filter-section">
            <label className="filter-label">Status</label>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
                onClick={() => setStatusFilter('approved')}
              >
                Approved
              </button>
              <button 
                className={`filter-btn ${statusFilter === 'not approved' ? 'active' : ''}`}
                onClick={() => setStatusFilter('not approved')}
              >
                Not Approved
              </button>
              <button 
                className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setStatusFilter('pending')}
              >
                Pending
              </button>
            </div>
          </div>

          <div className="filter-section">
            <label className="filter-label">Document Type</label>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${docTypeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setDocTypeFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${docTypeFilter === 'Insurance' ? 'active' : ''}`}
                onClick={() => setDocTypeFilter('Insurance')}
              >
                Insurance
              </button>
              <button 
                className={`filter-btn ${docTypeFilter === 'Title' ? 'active' : ''}`}
                onClick={() => setDocTypeFilter('Title')}
              >
                Title
              </button>
            </div>
          </div>

          <div className="filter-section">
            <label className="filter-label">Company</label>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${companyFilter === 'all' ? 'active' : ''}`}
                onClick={() => setCompanyFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${companyFilter === 'GRI' ? 'active' : ''}`}
                onClick={() => setCompanyFilter('GRI')}
              >
                GRI
              </button>
              <button 
                className={`filter-btn ${companyFilter === 'GRA' ? 'active' : ''}`}
                onClick={() => setCompanyFilter('GRA')}
              >
                GRA
              </button>
              <button 
                className={`filter-btn ${companyFilter === 'OP' ? 'active' : ''}`}
                onClick={() => setCompanyFilter('OP')}
              >
                OP
              </button>
              <button 
                className={`filter-btn ${companyFilter === 'KBHS' ? 'active' : ''}`}
                onClick={() => setCompanyFilter('KBHS')}
              >
                KBHS
              </button>
            </div>
          </div>
        </div>

        <div className="loans-list">
          {isLoadingLoans ? (
            <div className="no-loans">
              <span className="no-loans-icon">⏳</span>
              <p>Loading loans...</p>
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="no-loans">
              <span className="no-loans-icon">🔍</span>
              <p>{searchTerm ? 'No loans found for your search' : 'No loans match your filters'}</p>
            </div>
          ) : (
            filteredLoans.map((loan) => (
              <div 
                key={loan.id} 
                className="loan-card"
                onClick={() => handleLoanClick(loan)}
                title="Click to query this loan"
              >
                <div className="loan-header">
                  <span className="loan-number">{loan.company} • {loan.type}</span>
                  <span className={`loan-status status-${loan.status.toLowerCase()}`}>
                    {loan.status}
                  </span>
                </div>
                <div className="loan-meta">
                  <div className="loan-meta-row">
                    <span className="loan-meta-label">Date:</span>
                    <span className="loan-meta-value">{loan.date}</span>
                  </div>
                </div>
                <div className="loan-guid">
                  <span className="loan-guid-label">GUID:</span>
                  <span className="loan-guid-value">{loan.id}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Panel */}
      <div className="ai-chatbot">
        <div className="chatbot-header">
          <div className="header-content">
            <div className="header-icon">🤖</div>
            <div className="header-text">
              <h2>AI Loan Assistant</h2>
              <p className="header-subtitle">Ask me anything about your loans and documents</p>
            </div>
          </div>
          <button 
            className="clear-btn" 
            onClick={clearChat}
            title="Clear conversation"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>

        <div className="chatbot-messages">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message ${message.type} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-avatar">
                {message.type === 'user' ? '👤' : '🤖'}
              </div>
              <div className="message-content">
                <div className="message-text">
                    {message.type === 'bot' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    ) : (
                      message.content
                    )}
                  </div>
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message bot loading">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {messages.length === 1 && (
          <div className="suggestions-container">
            <p className="suggestions-title">Try asking:</p>
            <div className="suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        <form className="chatbot-input" onSubmit={sendMessage}>
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me about loans, documents, or approvals..."
            disabled={isLoading}
            autoFocus
          />
          <button 
            type="submit" 
            disabled={!inputMessage.trim() || isLoading}
            className="send-btn"
          >
            {isLoading ? '⏳' : <i className="fas fa-paper-plane"></i>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AIChatbot;
